/**
 * IndexedDB Storage Utility for Test Data Persistence
 * 
 * Uses idb-keyval for simple async IndexedDB operations.
 * Benefits over localStorage:
 * - Async (non-blocking, no UI freeze)
 * - Large capacity (500+ MB vs 5 MB)
 * - Reliable on mobile Safari
 */

import { get, set, del, keys } from 'idb-keyval';

// Key prefixes for different data types
const PENDING_DATA_PREFIX = 'test-data-pending-';
const PENDING_SUBMIT_PREFIX = 'test-submit-';

/**
 * Save test data to IndexedDB (async, non-blocking)
 * @param {string} examId - The exam ID
 * @param {object} data - Test data to save
 */
export const savePendingTestData = async (examId, data) => {
  try {
    const key = `${PENDING_DATA_PREFIX}${examId}`;
    const pendingData = {
      userExamId: examId,
      examDetail: data.examDetail,
      quesData: data.quesData,
      timestamp: new Date().toISOString(),
      retryCount: 0,
    };
    await set(key, pendingData);
    console.log('💾 Test data saved to IndexedDB:', key);
    return true;
  } catch (error) {
    console.error('❌ Failed to save to IndexedDB:', error);
    return false;
  }
};

/**
 * Save failed test submission to IndexedDB
 * @param {string} userExamId - The user exam ID
 * @param {object} submissionData - Submission data
 */
export const savePendingSubmission = async (userExamId, submissionData) => {
  try {
    const key = `${PENDING_SUBMIT_PREFIX}${userExamId}`;
    await set(key, {
      ...submissionData,
      timestamp: new Date().toISOString(),
      retryCount: 0,
    });
    console.log('💾 Pending submission saved to IndexedDB:', key);
    return true;
  } catch (error) {
    console.error('❌ Failed to save submission to IndexedDB:', error);
    return false;
  }
};

/**
 * Get all pending test data keys from IndexedDB
 * @returns {Promise<string[]>} Array of keys
 */
export const getPendingDataKeys = async () => {
  try {
    const allKeys = await keys();
    return allKeys.filter(key => 
      typeof key === 'string' && key.startsWith(PENDING_DATA_PREFIX)
    );
  } catch (error) {
    console.error('❌ Failed to get pending data keys:', error);
    return [];
  }
};

/**
 * Get all pending submission keys from IndexedDB
 * @returns {Promise<string[]>} Array of keys
 */
export const getPendingSubmitKeys = async () => {
  try {
    const allKeys = await keys();
    return allKeys.filter(key => 
      typeof key === 'string' && key.startsWith(PENDING_SUBMIT_PREFIX)
    );
  } catch (error) {
    console.error('❌ Failed to get pending submit keys:', error);
    return [];
  }
};

/**
 * Get pending data by key
 * @param {string} key - The IndexedDB key
 * @returns {Promise<object|null>} The stored data or null
 */
export const getPendingData = async (key) => {
  try {
    return await get(key);
  } catch (error) {
    console.error('❌ Failed to get data from IndexedDB:', error);
    return null;
  }
};

/**
 * Remove pending data after successful sync
 * @param {string} key - The key to remove
 */
export const removePendingData = async (key) => {
  try {
    await del(key);
    console.log('🗑️ Removed from IndexedDB:', key);
    return true;
  } catch (error) {
    console.error('❌ Failed to remove from IndexedDB:', error);
    return false;
  }
};

/**
 * Update retry count for a pending item
 * @param {string} key - The key to update
 */
export const incrementRetryCount = async (key) => {
  try {
    const data = await get(key);
    if (data) {
      data.retryCount = (data.retryCount || 0) + 1;
      data.timestamp = new Date().toISOString();
      await set(key, data);
      console.log(`🔄 Retry count updated to ${data.retryCount} for:`, key);
    }
  } catch (error) {
    console.error('❌ Failed to update retry count:', error);
  }
};

/**
 * Check if data size is small enough for sendBeacon (< 50KB)
 * @param {string} payload - JSON string payload
 * @returns {boolean} True if safe for sendBeacon
 */
export const isSafeForSendBeacon = (payload) => {
  const sizeInKB = new Blob([payload]).size / 1024;
  return sizeInKB < 50; // Leave buffer below 64KB limit
};
